import React, { useState, useEffect, useRef } from "react";
import { Card, Snackbar, Alert, Box, Typography } from "@mui/material";
import { CheckCircle, Warning, Error as ErrorIcon, Info } from "@mui/icons-material";

import DashboardLayout from "app/layout/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/layout/DashboardNavbar";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";

import { useSemestrales } from "./hooks/useSemestrales";
import HeaderSemestrales from "./components/HeaderSemestrales";
import TableSemestrales from "./components/TableSemestrales";
import logoImage from "../../../assets/images/logo horinzontal.png";

const Semestrales = () => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
    autoHideDuration: 6000,
  });

  const tableRef = useRef(null);

  const {
    records,
    filteredRecords,
    loading: loadingRecords,
    error: errorRecords,
    stats,
    terminales,
    commerces,
    currentCommerceName,
    hasPermission,
    permisosRegistros,
    changeCommerce,
    refreshData: refreshRecords,
    createCycle,
    closeCycle,
    hasData,
  } = useSemestrales();

  const isFirstLoad = useRef(true);
  const hasShownInitialMessage = useRef(false);

  const showNotification = (message, severity = "success", duration = 6000) => {
    setSnackbar({ open: true, message, severity, autoHideDuration: duration });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleRefresh = async () => {
    try {
      showNotification("Actualizando datos...", "info", 2000);
      await refreshRecords();
      showNotification("Datos actualizados correctamente", "success");
    } catch (err) {
      showNotification(`Error al actualizar: ${err.message}`, "error");
    }
  };

  const handleAddCycle = () => {
    if (tableRef.current) {
      tableRef.current.openCreateDialog();
    }
  };

  const handleCommerceChange = async (commerceId, commerceName) => {
    try {
      showNotification(`Cambiando a ${commerceName}...`, "info", 2000);
      await changeCommerce(commerceId, commerceName);
      showNotification(`Propiedad cambiada a ${commerceName}`, "success");
    } catch (error) {
      showNotification(`Error al cambiar propiedad: ${error.message}`, "error");
    }
  };

  const handleCloseCycle = async (id, plate, exitDate, observation) => {
    try {
      const result = await closeCycle(id, plate, exitDate, observation);
      if (result && result.success) {
        showNotification("Ciclo cerrado exitosamente", "success");
        return result;
      } else {
        const errorMsg = result?.error || "Error al cerrar ciclo";
        showNotification(errorMsg, "error");
        return { success: false, error: errorMsg };
      }
    } catch (error) {
      console.error("❌ Error en handleCloseCycle:", error);
      showNotification("Error al cerrar ciclo", "error");
      return { success: false, error: error.message };
    }
  };

  const handleCreateCycle = async (plate, date, observation) => {
    try {
      const result = await createCycle(plate, date, observation);
      if (result && result.success) {
        showNotification("Ciclo creado exitosamente", "success");
        return result;
      } else {
        const errorMsg = result?.error || "Error al crear ciclo";
        showNotification(errorMsg, "error");
        return { success: false, error: errorMsg };
      }
    } catch (error) {
      console.error("❌ Error en handleCreateCycle:", error);
      showNotification("Error al crear ciclo", "error");
      return { success: false, error: error.message };
    }
  };

  useEffect(() => {
    if (errorRecords) {
      showNotification(errorRecords, "error", 8000);
    }
  }, [errorRecords]);

  useEffect(() => {
    if (
      isFirstLoad.current &&
      !loadingRecords &&
      hasData &&
      !errorRecords &&
      !hasShownInitialMessage.current
    ) {
      hasShownInitialMessage.current = true;
      showNotification(`Cargados ${records.length} registros`, "info", 5000);
    }
  }, [loadingRecords, hasData, errorRecords, records.length]);

  useEffect(() => {
    if (isFirstLoad.current && !loadingRecords) {
      isFirstLoad.current = false;
    }
  }, [loadingRecords]);

  // Si no tiene permisos, mostrar mensaje con logo
  if (!hasPermission && !loadingRecords) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3}>
          <Card
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: "12px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            }}
          >
            <Box
              component="img"
              src={logoImage}
              alt="GoPass"
              sx={{
                height: 80,
                width: "auto",
                objectFit: "contain",
                mb: 3,
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            <Typography variant="h5" fontWeight="bold" color="text.secondary" gutterBottom>
              No tiene permisos para ver este módulo
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Contacte al administrador del sistema para solicitar acceso.
            </Typography>
          </Card>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.autoHideDuration}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={handleCloseSnackbar}
          sx={{ width: "100%", maxWidth: "500px", borderRadius: "8px" }}
          iconMapping={{
            success: <CheckCircle fontSize="inherit" />,
            error: <ErrorIcon fontSize="inherit" />,
            warning: <Warning fontSize="inherit" />,
            info: <Info fontSize="inherit" />,
          }}
        >
          <MDTypography variant="body2" fontWeight="medium">
            {snackbar.message}
          </MDTypography>
        </Alert>
      </Snackbar>

      <HeaderSemestrales
        onRefresh={handleRefresh}
        loading={loadingRecords}
        stats={stats}
        onAddCycle={handleAddCycle}
        commerceName={currentCommerceName}
        commerces={commerces}
        onCommerceChange={handleCommerceChange}
        hasCreatePermission={permisosRegistros.entrada}
      />

      <MDBox py={3}>
        <Card
          sx={{
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 4px 20px 0 rgba(0,0,0,0.1)",
            border: errorRecords ? "1px solid #f44336" : "1px solid #e0e0e0",
          }}
        >
          <MDBox p={3} bgcolor="background.paper">
            <TableSemestrales
              ref={tableRef}
              records={filteredRecords}
              loading={loadingRecords}
              error={errorRecords}
              onRefresh={refreshRecords}
              onCloseCycle={handleCloseCycle}
              onAddCycle={handleCreateCycle}
              terminales={terminales}
              hasCreatePermission={permisosRegistros.entrada}
              hasClosePermission={permisosRegistros.salida}
            />
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
};

export default Semestrales;
